import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import { Box } from '@mui/material';

import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';

import { Offer } from '../../../graphql/API';
import { getOfferColumns, OfferColumnVariant } from './Column';

type Props = {
  data: Offer[];
  variant?: OfferColumnVariant;
  requestCounts?: Record<string, number>;
};

export const OffersTable: React.FC<Props> = ({ data, variant = 'offer', requestCounts }) => {
  const navigate = useNavigate();
  const basePath = variant === 'product' ? 'products' : 'offers';

  const memoizedColumns = React.useMemo(
    () => getOfferColumns(variant, requestCounts),
    [variant, requestCounts],
  );
  const memoizedData = React.useMemo(() => data, [data]);

  const table = useMaterialReactTable({
    enableRowSelection: false,
    enableStickyHeader: true,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableColumnActions: false,
    enableColumnFilters: false,
    enablePagination: false,
    enableSorting: true,
    muiTableHeadProps: { sx: { opacity: 1 } },
    muiTableHeadRowProps: {
      sx: { backgroundColor: '#FCFCFC', boxShadow: 'none' },
    },
    muiTableHeadCellProps: {
      sx: ({ custom }) => ({
        color: 'text.secondary',
        borderColor: custom.colors.brandLightGrey,
        fontWeight: 400,
        '& .Mui-TableHeadCell-Content-Wrapper': { fontWeight: 400 },
      }),
    },
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => navigate(`/${basePath}/${row.original.id}`),
      sx: {
        cursor: 'pointer',
        backgroundColor: 'inherit',
        '&:hover': { backgroundColor: '#FAFAFA' },
      },
    }),
    muiTableBodyCellProps: {
      sx: ({ custom }) => ({ borderColor: custom.colors.brandLightGrey }),
    },
    muiTablePaperProps: { sx: { boxShadow: 'none' } },
    muiTableContainerProps: { sx: { backgroundColor: 'white' } },
    columns: memoizedColumns,
    data: memoizedData,
    getRowId: (row) => row.id,
  });

  return (
    <Box sx={{ borderRadius: '8px', border: '1px solid #EBEBEB', overflow: 'hidden' }}>
      <MaterialReactTable table={table} />
    </Box>
  );
};

export default OffersTable;
